import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import Shortcut from "../../framework/Shortcut";
import Checkbox from "../../framework/Checkbox";
import images from "../../assets/images";
import classes from "./ColumnFilters.module.css";
import GlobalContext from "../../framework/GlobalContext";
import { executeScroll, isEmpty } from "../../utils/utils";
import { _ } from "ag-grid-community";

const ColumnFilters = ({ columnDefs = [], onChange = () => {} }) => {
  const { locked } = images;
  const [activeIndex, setActiveIndex] = useState(0);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);
  const [isChecked, setIsChecked] = useState([]);
  const [isShowAllChecked, setIsShowAllChecked] = useState(false);
  const [updatedColumnDefs, setUpdatedColumnDefs] = useState(columnDefs);
  const listRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceOnChange = useCallback(_.debounce(onChange, 300), []);

  const changeHandler = useCallback(
    (checked, index, isLocked) => {
      if (isLocked) return;
      setIsChecked((prev) => {
        prev[index] = checked;
        return [...prev];
      });
      setUpdatedColumnDefs((prev) => {
        prev[index].hide = !checked;
        return prev;
      });
      debounceOnChange(updatedColumnDefs);
    },
    [debounceOnChange, updatedColumnDefs]
  );
  const showAllColumns = useCallback(() => {
    columnDefs?.forEach((cd, index) => {
      changeHandler(!isShowAllChecked, index, cd.locked);
    });
    setIsShowAllChecked((prev) => !prev);
  }, [changeHandler, columnDefs, isShowAllChecked]);
  const listener = useCallback(
    (e) => {
      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey;
      const alt = e.altKey;
      if (key === "arrowdown") {
        if (activeIndex === columnDefs?.length - 1) {
          return;
        }
        setActiveIndex((prev) => prev + 1);
        if (ctrl)
          changeHandler(
            !isChecked[activeIndex],
            activeIndex,
            columnDefs[activeIndex].locked
          );
        executeScroll(listRef);
        return;
      }
      if (key === "arrowup") {
        if (activeIndex === 0) {
          return;
        }
        setActiveIndex((prev) => prev - 1);
        if (ctrl)
          changeHandler(
            !isChecked[activeIndex],
            activeIndex,
            columnDefs[activeIndex].locked
          );
        executeScroll(listRef);
        return;
      }
      if (alt && key === "d") {
        showAllColumns();
        return;
      }
    },
    [activeIndex, changeHandler, columnDefs, isChecked, showAllColumns]
  );
  useEffect(() => {
    if (currentComponent === "list_nav_column_filter")
      document.addEventListener("keyup", listener, false);
    return () => {
      document.removeEventListener("keyup", listener, false);
    };
  }, [currentComponent, listener]);
  useEffect(() => {
    setCurrentComponent("list_nav_column_filter");
  }, [setCurrentComponent]);
  useEffect(() => {
    if (columnDefs?.length) {
      columnDefs.forEach((cd) => {
        setIsChecked((prev) => [...prev, !cd.hide]);
      });
    }
  }, [columnDefs]);
  return (
    <div className={classes["column-filters"]}>
      <div className={classes["column-filters-header"]}>
        <div className={classes["column-filters-header-content"]}>
          <Checkbox
            label="Select All"
            checked={isShowAllChecked}
            onChange={showAllColumns}
          />
          <Shortcut keys={["Alt", "D"]} />
        </div>
      </div>
      <div className={classes["column-filters-content"]}>
        <ul className={classes["column-filters-list"]}>
          {columnDefs?.map((cd, index) => {
            return (
              <React.Fragment key={cd.field}>
                {!isEmpty(cd.headerName) && (
                  <li
                    ref={index === activeIndex ? listRef : null}
                    className={`${classes["column-filters-list-item"]} ${
                      index === activeIndex && classes["active"]
                    }`}
                    onClick={(e) => setActiveIndex(index)}
                  >
                    <div
                      className={`${classes["column-filters-list-item-content"]}`}
                    >
                      <Checkbox
                        label={cd.headerName}
                        checked={isChecked[index] || false}
                        disabled={cd.locked}
                        onChange={(e) => {
                          changeHandler(e.target.checked, index, cd.locked);
                        }}
                      />
                      {cd.locked && (
                        <div className={"column-filter-item-locked"}>
                          <img src={locked} alt="locked" />
                        </div>
                      )}
                    </div>
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default ColumnFilters;
